import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Shield, CheckCircle, XCircle, Calendar, Globe } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminCookieConsents = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/cookie-consent/logs`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/cookie-consent/logs/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching cookie consent data:', error);
      toast.error('Fehler beim Laden der Cookie-Daten');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-6" style={{ color: '#E6EDF3' }}>Lädt...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3" style={{ color: '#E6EDF3' }}>
          <Shield className="w-8 h-8" style={{ color: '#7CD4FD' }} />
          Cookie-Consent Protokoll
        </h1>
        <p className="mt-2" style={{ color: '#94A3B8' }}>
          DSGVO-konformes Logging aller Cookie-Zustimmungen
        </p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card style={{ backgroundColor: '#1A1F2E', borderColor: '#2A3142' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: '#94A3B8' }}>Gesamt</p>
                  <p className="text-2xl font-bold" style={{ color: '#7CD4FD' }}>{stats.total}</p>
                </div>
                <Globe className="w-8 h-8" style={{ color: '#7CD4FD', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>

          <Card style={{ backgroundColor: '#1A1F2E', borderColor: '#2A3142' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: '#94A3B8' }}>Akzeptiert</p>
                  <p className="text-2xl font-bold" style={{ color: '#22C55E' }}>{stats.accepted}</p>
                </div>
                <CheckCircle className="w-8 h-8" style={{ color: '#22C55E', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>

          <Card style={{ backgroundColor: '#1A1F2E', borderColor: '#2A3142' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: '#94A3B8' }}>Abgelehnt</p>
                  <p className="text-2xl font-bold" style={{ color: '#EF4444' }}>{stats.declined}</p>
                </div>
                <XCircle className="w-8 h-8" style={{ color: '#EF4444', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>

          <Card style={{ backgroundColor: '#1A1F2E', borderColor: '#2A3142' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: '#94A3B8' }}>Akzeptanzrate</p>
                  <p className="text-2xl font-bold" style={{ color: '#7CD4FD' }}>
                    {stats.acceptance_rate.toFixed(1)}%
                  </p>
                </div>
                <Shield className="w-8 h-8" style={{ color: '#7CD4FD', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Logs Table */}
      <Card style={{ backgroundColor: '#1A1F2E', borderColor: '#2A3142' }}>
        <CardHeader>
          <CardTitle style={{ color: '#E6EDF3' }}>Consent Logs</CardTitle>
          <CardDescription style={{ color: '#94A3B8' }}>
            Alle registrierten Cookie-Zustimmungen mit IP-Adresse und Zeitstempel
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid #2A3142' }}>
                  <th className="text-left p-3 text-sm font-semibold" style={{ color: '#94A3B8' }}>Datum</th>
                  <th className="text-left p-3 text-sm font-semibold" style={{ color: '#94A3B8' }}>IP-Adresse</th>
                  <th className="text-left p-3 text-sm font-semibold" style={{ color: '#94A3B8' }}>Status</th>
                  <th className="text-left p-3 text-sm font-semibold" style={{ color: '#94A3B8' }}>Seite</th>
                  <th className="text-left p-3 text-sm font-semibold" style={{ color: '#94A3B8' }}>User Agent</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, index) => (
                  <tr 
                    key={log.id} 
                    style={{ 
                      borderBottom: index < logs.length - 1 ? '1px solid #2A3142' : 'none'
                    }}
                  >
                    <td className="p-3 text-sm" style={{ color: '#E6EDF3' }}>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" style={{ color: '#7CD4FD' }} />
                        {new Date(log.consent_date).toLocaleString('de-DE')}
                      </div>
                    </td>
                    <td className="p-3 text-sm font-mono" style={{ color: '#E6EDF3' }}>
                      {log.ip_address}
                    </td>
                    <td className="p-3">
                      <span 
                        className="px-3 py-1 rounded-full text-xs font-bold"
                        style={{
                          backgroundColor: log.consent_type === 'accepted' ? '#22C55E20' : '#EF444420',
                          color: log.consent_type === 'accepted' ? '#22C55E' : '#EF4444'
                        }}
                      >
                        {log.consent_type === 'accepted' ? 'Akzeptiert' : 'Abgelehnt'}
                      </span>
                    </td>
                    <td className="p-3 text-sm" style={{ color: '#94A3B8' }}>
                      {log.page_url ? new URL(log.page_url).pathname : '-'}
                    </td>
                    <td className="p-3 text-xs" style={{ color: '#94A3B8', maxWidth: '300px' }}>
                      <div className="truncate">{log.user_agent}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {logs.length === 0 && (
            <div className="text-center py-12">
              <Shield className="w-16 h-16 mx-auto mb-4" style={{ color: '#7CD4FD', opacity: 0.3 }} />
              <p style={{ color: '#94A3B8' }}>Noch keine Cookie-Consents protokolliert</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card style={{ backgroundColor: '#1A1F2E', borderColor: '#2A3142' }}>
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 mt-1" style={{ color: '#7CD4FD' }} />
            <div>
              <h3 className="font-semibold mb-2" style={{ color: '#E6EDF3' }}>
                DSGVO-Konformität
              </h3>
              <p className="text-sm" style={{ color: '#94A3B8' }}>
                Alle Cookie-Zustimmungen werden mit IP-Adresse, Zeitstempel und User-Agent protokolliert. 
                Diese Daten dienen als Nachweis für die DSGVO-Compliance und können bei Bedarf 
                (z.B. bei behördlichen Anfragen) vorgelegt werden. Die Logs werden für 3 Jahre aufbewahrt.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
